import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import { FlagIcon } from './ReportIssueButton';
import { useIssueReport } from './IssueReportProvider';

/*
 * Report an issue, from anywhere (2026-10-15): a narrow tab on the screen's
 * inline-end edge, above every dialog, drawer, viewer and tool (z-index 3500:
 * the highest dialog is the photo annotator at 3200; the View-as banner, at
 * 4000, stays above it), so a problem met inside a modal or halfway through a
 * workflow is reported from there, with that dialog named on the report.
 *
 * Mid-edge, small and translucent until hovered or focused: clear of bottom
 * bars, sticky Save footers, toasts and map controls. Dialogs that keep Tab
 * inside add it to their cycle (utils/focusTrap.js `extra`), so a keyboard
 * reaches it too.
 *
 * Shown to whoever may report (issue:report: every signed-in role but
 * `public`; viewing as a role included, the server accepts those reports).
 * Not in Field Mode, whose screens carry a flag in their header and whose
 * camera has its own chip, nor in the analysis workbench (/tools/analysis),
 * whose top bar has its own and whose end drawers lie where the tab would
 * be; and hidden, not removed, while the report is open,
 * so focus can come back to it.
 */
export default function ReportIssueTab() {
  const { t } = useTranslation('issues');
  const { user } = useAuth() || {};
  const report = useIssueReport();
  const { pathname } = useLocation();
  if (!report || !user || (user.roles || []).includes('public') || !can(user, 'issue:report')) return null;
  if (pathname === '/field' || pathname.startsWith('/field/')) return null;
  if (pathname === '/tools/analysis') return null;
  const tab = (
    <button type="button" className="report-issue-tab" data-report-issue-tab="" data-testid="report-issue-tab"
      hidden={report.isOpen} onClick={report.openReport} aria-haspopup="dialog"
      aria-label={t('open')} title={t('tip.open')} style={tabStyle}>
      <FlagIcon />
    </button>
  );
  return typeof document !== 'undefined' && document.body ? createPortal(tab, document.body) : tab;
}

const tabStyle = {
  position: 'fixed',
  insetInlineEnd: 0,
  top: '58%',
  transform: 'translateY(-50%)',
  zIndex: 3500,
  width: 30,
  height: 48,
  padding: 0,
  border: '1px solid var(--c-primary-border-2, rgba(0,0,0,0.15))',
  borderInlineEnd: 'none',
  borderStartStartRadius: 10,
  borderEndStartRadius: 10,
  background: 'var(--c-primary-2)',
  color: 'var(--c-on-accent)',
  boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
  cursor: 'pointer',
};
